// ═══════════════════════════════════════════════════════════
// ARAMS UI — Textarea Component
// ═══════════════════════════════════════════════════════════

import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const textareaVariants = cva(
  'flex w-full rounded-lg text-surface-200 placeholder-surface-500 transition-all focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-none',
  {
    variants: {
      variant: {
        default:
          'bg-surface-900 border border-surface-700 focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500',
        ghost: 'bg-transparent border-none focus:ring-0',
        filled:
          'bg-surface-800/80 border border-transparent focus:bg-surface-900 focus:border-surface-600 focus:ring-2 focus:ring-brand-500/30',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  label?: string;
  error?: string;
  hint?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, label, error, hint, id, ...props }, ref) => {
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="space-y-1.5">
        {label && (
          <label htmlFor={textareaId} className="text-sm font-medium text-surface-300">
            {label}
          </label>
        )}
        <textarea
          id={textareaId}
          ref={ref}
          className={cn(
            textareaVariants({ variant }),
            'min-h-[80px] px-3.5 py-2.5 text-sm',
            error && 'border-danger focus:ring-danger/50 focus:border-danger',
            className,
          )}
          {...props}
        />
        {error && <p className="text-xs text-danger">{error}</p>}
        {hint && !error && <p className="text-xs text-surface-500">{hint}</p>}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
export { Textarea, textareaVariants };
